import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigation } from "../../../utils/navigation";
import { useNavigate } from "react-router-dom";
import SimpleContext from "../../../context/SimpleContext";
import { AuthUserContext } from "../../../context/AuthUserContext";
const fadeInTop = keyframes`
  0% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);
    opacity: 0.4;
  }
  100% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
    opacity: 1;
  }
`;

const fadeOutBottom = keyframes`
  0% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
    opacity: 1;
  }
  100% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);
    opacity: 0;
  }
`;
const Container = styled.div`
  display: none;
  background-color: var(--bg-200);
  width: 100%;
  max-width: 100vw;
  height: var(--navbar-mini);

  align-items: center;
  justify-content: center;
  @media (min-width: 768px) {
    display: flex;
  }
`;
const Wrapper = styled.header`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    max-width: var(--max-width-container);
    padding: 0;
    display: grid; /* Prikazuje sve elemente odjednom na širim ekranima */
    grid-template-columns: 2fr 1fr;
    gap: 2px;
  }
`;
const ContainerLeft = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-content: center;
  gap: 10px;

  @media (min-width: 1025px) {
    gap: 24px;
  }
`;
const ContainerRight = styled(ContainerLeft)`
  justify-content: flex-end;
`;
const GridItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: var(--text-100);
  height: 100%;
  position: relative;
  cursor: pointer;
  font-size: var(--font-size-small);
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 22%;
    width: 100%;
    height: 2px;
    background-color: var(--accent-200, var(--text-100));
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: var(--accent-200, var(--text-100));
  }
  &:hover::after,
  &.active::after {
    transform: scaleX(1);
  }

  svg {
    transition: transform 0.2s ease;
  }
  &.active svg {
    transform: rotate(180deg);
  }

  @media (min-width: 1025px) {
    font-size: var(--font-size-base);
  }
`;

const SubHeader = styled.div`
  position: absolute;
  background-color: var(--bg-100);
  color: var(--text-100);

  top: 100%;
  left: 0;
  box-shadow: var(--shadow-large);
  padding: 12px;
  z-index: 1;

  overflow-y: auto;
  max-height: 70vh;
  min-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--bg-300);
  ${({ $isVisible }) =>
    $isVisible
      ? css`
          animation: ${fadeInTop} 0.3s ease-in forwards; // Duže trajanje za slide-in
        `
      : css`
          animation: ${fadeOutBottom} 0.2s ease-in forwards; // Kraće trajanje za slide-out
        `}
`;

const SubHeaderWrapper = styled.div`
  @media (min-width: 768px) {
    max-width: var(--max-width-container);
    padding: 0;
    display: grid; /* Prikazuje sve elemente odjednom na širim ekranima */
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    width: 100%;
  }
`;

const SubHeaderItem = styled.div`
  width: 94%;
  padding: 12px 14px;
  margin: 1px 0;
  border-radius: 6px;
  cursor: pointer;

  font-weight: 400;
  font-size: var(--font-size-base);
  color: var(--text-100);
  transition: background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease;

  &:hover {
    background-color: var(--bg-200);
    color: var(--accent-200, var(--text-100));
    padding-left: 20px;
  }
`;
const DesktopHeader = () => {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { categories } = useContext(ProductContext);
  const { authTokens } = useContext(AuthUserContext);
  const { goToCategory, goToAccount, goToHome } = useNavigation();
  const { showLoginPopup } = useContext(SimpleContext);
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const sliderItems = [
    {
      id: 1,
      text: t("HEADER.LINKS.NICOTINE_POUCHES"),
      url: `/${i18n.language}/snus-verkauf`,
    },
    {
      id: 2,
      text: t("HEADER.LINKS.BRANDS"),
      url: `/${i18n.language}/unsere-marken`,
    },
    {
      id: 3,
      text: t("HEADER.LINKS.BEST_SELLERS"),
      url: `/${i18n.language}/best-seller`,
    },
    {
      id: 4,
      text: t("HEADER.LINKS.NEW_ARRIVALS"),
      url: `/${i18n.language}/neuheiten`,
    },
    { id: 5, text: t("HEADER.LINKS.BLOG"), url: `/` },
  ];

  // Provera da li je ekran mobilni ili desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Container>
      <Wrapper>
        <ContainerLeft>
          {sliderItems.map((item) => (
            <GridItem
              key={item.id}
              className={isVisible === item.id ? "active" : ""}
              onMouseEnter={() => item.id === 2 && setIsVisible(item.id)}
              onMouseLeave={() => item.id === 2 && setIsVisible(false)}
              onClick={() => navigate(item.url)}
            >
              {item.text}
              {item.id === 2 && (
                <svg
                  fill="currentColor"
                  width="16px"
                  height="16px"
                  viewBox="0 0 1024 1024"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M759.2 419.8L697.4 358 512 543.4 326.6 358l-61.8 61.8L512 667z" />
                </svg>
              )}
            </GridItem>
          ))}
        </ContainerLeft>
        <ContainerRight>
          <GridItem
            onClick={() => {
              if (authTokens) {
                navigate(goToAccount());
              } else {
                showLoginPopup();
              }
            }}
          >
            {authTokens ? t("HEADER.PROFILE") : t("HEADER.SIGN_UP")}
          </GridItem>
          <GridItem>{t("HEADER.CUSTOMER_SERVICE")}</GridItem>
        </ContainerRight>
        {isVisible && isVisible === 2 && (
          <SubHeader
            onMouseEnter={() => setIsVisible(2)}
            onMouseLeave={() => setIsVisible(false)}
            $isVisible={isVisible}
          >
            <SubHeaderWrapper>
              {categories.map((category, index) => (
                <SubHeaderItem
                  key={index}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(goToCategory(category.name));
                  }}
                >
                  {category.name}
                </SubHeaderItem>
              ))}
            </SubHeaderWrapper>
          </SubHeader>
        )}
      </Wrapper>
    </Container>
  );
};

export default DesktopHeader;
